import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Plug, Trash2, CheckCircle, AlertTriangle, Copy, Eye, EyeOff, ExternalLink } from 'lucide-react';
import { qboOAuth } from '../services/api';

/**
 * Accounting > QuickBooks - connect each QuickBooks Online company to CPx.
 *
 * One row per company, because there are four sets of books (Crab Place, Crab &
 * Cruise, and the two real-estate LLCs) and each consents separately. Intercompany
 * matching - the thing QuickBooks structurally cannot do, since each company file
 * is an island - only works when all four are connected and current.
 *
 * The page derives nothing. Connection health, expiry and error text all come from
 * the server, so this screen and the daily cron can never disagree about whether a
 * company is connected.
 */

const fmtDate = (v) => {
  if (!v) return '--';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '--';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const daysUntil = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  return Math.round((d.getTime() - Date.now()) / 86400000);
};

export default function QuickBooks() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(null);

  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [environment, setEnvironment] = useState('production');
  const [savingCreds, setSavingCreds] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await qboOAuth('status');
      setStatus(data);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The consent happens in another tab. Re-check when this one regains focus so a
  // finished connection appears without anyone hunting for a refresh button.
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const saveCreds = async () => {
    if (!clientId.trim() && !clientSecret.trim()) return;
    setSavingCreds(true);
    try {
      await qboOAuth('set-config', {
        clientId: clientId.trim() || undefined,
        clientSecret: clientSecret.trim() || undefined,
      });
      setClientId('');
      setClientSecret('');
      setNotice('Credentials saved.');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingCreds(false);
    }
  };

  const connect = async () => {
    setBusy('connect');
    try {
      const res = await qboOAuth('auth-url', { environment });
      if (res.needsSetup) { setError('Add the Client ID and Secret first.'); return; }
      window.open(res.url, '_blank', 'noopener');
      setNotice('Pick a company on the Intuit tab, then come back here.');
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const act = async (action, realmId, confirmText) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(realmId + ':' + action);
    setError(null);
    setNotice(null);
    try {
      const res = await qboOAuth(action, { realmId });
      if (action === 'test') {
        setNotice(res.ok
          ? 'Read a live response from ' + (res.companyName || 'QuickBooks') + '. The connection works.'
          : 'That read failed: ' + res.error);
      } else if (action === 'refresh') {
        setNotice(res.ok ? 'Token refreshed.' : 'Refresh failed: ' + res.error);
      } else if (action === 'disconnect') {
        setNotice('Disconnected.');
      }
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => setNotice('Copied.')).catch(() => {});
  };

  const connections = (status && status.connections) || [];
  const needsSetup = status && status.needsSetup;

  return (
    <div className="page-content">
      <div className="breadcrumb">
        <span>Accounting</span>
        <span> / </span>
        <span>QuickBooks</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h1 style={{ color: 'var(--navy)', margin: 0 }}>QuickBooks</h1>
        <button className="btn btn-ghost" onClick={load} disabled={loading}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {error && (
        <div className="card" style={{ borderLeft: '4px solid var(--orange)', marginBottom: 16 }}>
          <div className="card-body" style={{ color: 'var(--orange)' }}>{error}</div>
        </div>
      )}
      {notice && (
        <div className="card" style={{ borderLeft: '4px solid var(--navy)', marginBottom: 16 }}>
          <div className="card-body">{notice}</div>
        </div>
      )}

      {/* Setup */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <h3>Intuit app credentials</h3>
        </div>
        <div className="card-body">
          <p style={{ color: '#6e6e73', fontSize: 13, marginTop: 0 }}>
            From the app you created at developer.intuit.com. Use the <strong>Production</strong> keys for
            the real books; the Development keys only ever reach Intuit's demo company.
          </p>

          <div className="form-group">
            <label className="form-label">Redirect URI (must be registered on the Intuit app, exactly)</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                className="form-input"
                readOnly
                value={(status && status.redirectUri) || ''}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />
              <button className="btn btn-outline" onClick={() => copy(status.redirectUri)}>
                <Copy size={14} />
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">
                Client ID {status && status.hasClientId && <span style={{ color: '#2e7d32' }}>(saved)</span>}
              </label>
              {/* ⚠️ A TEXT INPUT FOLLOWED BY A PASSWORD INPUT LOOKS LIKE A LOGIN FORM
                  TO EVERY BROWSER, and Chrome duly autofilled these two with the
                  user's own email and password. That is not a cosmetic annoyance:
                  it silently overwrote a valid Client ID with an email address and
                  stored a personal login password as the client secret. Hence
                  name="qbo-client-id" rather than anything username-shaped,
                  autoComplete="off" here and "new-password" on the secret (which is
                  the only value Chrome reliably honours on a password field), and
                  data-lpignore/data-1p-ignore for the password managers that ignore
                  the standard attribute entirely. The server validates the shape too,
                  because none of this is guaranteed. */}
              <input
                className="form-input"
                name="qbo-client-id"
                autoComplete="off"
                data-lpignore="true"
                data-1p-ignore="true"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                placeholder={status && status.hasClientId ? 'Saved. Paste to replace.' : 'Paste the Client ID'}
              />
            </div>
            <div className="form-group">
              <label className="form-label">
                Client Secret {status && status.hasClientSecret && <span style={{ color: '#2e7d32' }}>(saved)</span>}
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="form-input"
                  name="qbo-client-secret"
                  autoComplete="new-password"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  type={showSecret ? 'text' : 'password'}
                  value={clientSecret}
                  onChange={(e) => setClientSecret(e.target.value)}
                  placeholder={status && status.hasClientSecret ? 'Saved. Paste to replace.' : 'Paste the Client Secret'}
                />
                <button className="btn btn-outline" onClick={() => setShowSecret(!showSecret)} type="button">
                  {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
          </div>

          <button className="btn btn-navy" onClick={saveCreds} disabled={savingCreds || (!clientId.trim() && !clientSecret.trim())}>
            {savingCreds ? 'Saving...' : 'Save credentials'}
          </button>
        </div>
      </div>

      {/* Connect */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <h3>Connect a company</h3>
        </div>
        <div className="card-body">
          <p style={{ color: '#6e6e73', fontSize: 13, marginTop: 0 }}>
            Each set of books consents separately, so run this once per company. CPx only ever
            reads from QuickBooks. It cannot create, change or delete anything.
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Which QuickBooks</label>
              <select className="form-select" value={environment} onChange={(e) => setEnvironment(e.target.value)}>
                <option value="production">Production (the real books)</option>
                <option value="sandbox">Sandbox (Intuit's demo company)</option>
              </select>
            </div>
            <button className="btn btn-primary" onClick={connect} disabled={busy === 'connect' || needsSetup}>
              <Plug size={14} /> {busy === 'connect' ? 'Opening Intuit...' : 'Connect'}
            </button>
            <a
              className="btn btn-ghost"
              href="https://developer.intuit.com/app/developer/dashboard"
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink size={14} /> Intuit developer dashboard
            </a>
          </div>
          {needsSetup && (
            <p style={{ color: 'var(--orange)', fontSize: 13 }}>Save the Client ID and Secret above first.</p>
          )}
        </div>
      </div>

      {/* Connections */}
      <div className="card">
        <div className="card-header">
          <h3>Connected companies {connections.length ? '(' + connections.length + ')' : ''}</h3>
        </div>
        <div className="card-body">
          {loading && <p style={{ color: '#6e6e73' }}>Loading...</p>}
          {!loading && connections.length === 0 && (
            <p style={{ color: '#6e6e73' }}>Nothing connected yet.</p>
          )}
          {connections.length > 0 && (
            <table className="product-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Status</th>
                  <th>Last refreshed</th>
                  <th>Access expires</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {connections.map((c) => {
                  const days = daysUntil(c.refresh_expires_at);
                  const healthy = c.status === 'connected';
                  return (
                    <tr key={c.realm_id}>
                      <td>
                        <strong>{c.company_name || 'Company ' + c.realm_id}</strong>
                        <div style={{ fontSize: 11, color: '#6e6e73' }}>
                          id {c.realm_id}
                          {c.environment === 'sandbox' && (
                            <span style={{ marginLeft: 6, background: '#f0f0f5', padding: '1px 6px', borderRadius: 4 }}>
                              sandbox
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        {healthy ? (
                          <span style={{ color: '#2e7d32', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <CheckCircle size={14} /> Connected
                          </span>
                        ) : (
                          <span style={{ color: 'var(--orange)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <AlertTriangle size={14} />
                            {c.status === 'needs_reconnect' ? 'Needs reconnecting' : 'Error'}
                          </span>
                        )}
                        {c.last_error && (
                          <div style={{ fontSize: 11, color: '#6e6e73', maxWidth: 320 }}>{c.last_error}</div>
                        )}
                      </td>
                      <td style={{ fontSize: 12 }}>{fmtDate(c.last_refresh_at)}</td>
                      <td style={{ fontSize: 12 }}>
                        {days == null ? '--' : (
                          <span style={{ color: days < 14 ? 'var(--orange)' : 'inherit' }}>
                            {days} day{days === 1 ? '' : 's'}
                          </span>
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button
                          className="btn btn-ghost"
                          onClick={() => act('test', c.realm_id)}
                          disabled={busy === c.realm_id + ':test'}
                        >
                          Test
                        </button>
                        <button
                          className="btn btn-ghost"
                          onClick={() => act('refresh', c.realm_id)}
                          disabled={busy === c.realm_id + ':refresh'}
                        >
                          <RefreshCw size={13} />
                        </button>
                        <button
                          className="btn btn-ghost"
                          onClick={() => act('disconnect', c.realm_id,
                            'Disconnect ' + (c.company_name || c.realm_id) + '? Nothing in QuickBooks changes. CPx just stops being able to read this company until somebody reconnects it.')}
                          disabled={busy === c.realm_id + ':disconnect'}
                        >
                          <Trash2 size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <p style={{ color: '#6e6e73', fontSize: 12, marginBottom: 0, marginTop: 16 }}>
            A QuickBooks connection expires about 100 days after it was last used, so CPx refreshes
            every company once a day whether or not anyone is reading. If one ever does lapse, Greg
            and Mercedes get an email naming the company, and reconnecting it takes one click here.
          </p>
        </div>
      </div>
    </div>
  );
}
